/**
 * Input Component
 *
 * Componente de input reutilizável com suporte a dark mode, labels,
 * mensagens de erro, texto de ajuda e ícones.
 *
 * Este arquivo também exporta: Textarea, Select, Checkbox, DatePicker
 * RadioGroup é exportado de ./RadioGroup (shadcn/ui)
 *
 * @module components/ui
 * @since 2025-01-03
 *
 * @example
 * // Input básico com label
 * <Input label="Nome" placeholder="Digite seu nome" />
 *
 * @example
 * // Input com erro de validação
 * <Input
 *   label="Email"
 *   type="email"
 *   error={errors.email?.message}
 *   {...register('email')}
 * />
 *
 * @example
 * // Input com ícone de busca
 * <Input
 *   leftIcon={<SearchIcon />}
 *   placeholder="Buscar..."
 *   onChange={handleSearch}
 * />
 *
 * @example
 * // Input com texto de ajuda
 * <Input
 *   label="Senha"
 *   type="password"
 *   helperText="Mínimo de 8 caracteres"
 * />
 *
 * @example
 * // Select com opções
 * <Select
 *   label="Estado"
 *   options={[
 *     { value: 'SP', label: 'São Paulo' },
 *     { value: 'RJ', label: 'Rio de Janeiro' },
 *   ]}
 *   value={state}
 *   onChange={(e) => setState(e.target.value)}
 * />
 *
 * @example
 * // Checkbox com label
 * <Checkbox label="Aceito os termos" checked={accepted} onChange={setAccepted} />
 *
 * @example
 * // DatePicker com hora
 * <DatePicker label="Data do evento" value={date} onChange={setDate} showTime />
 */

import React, { forwardRef, type InputHTMLAttributes } from 'react';
import { cn } from '@/lib/utils';

// RadioGroup é agora definido em ./RadioGroup (shadcn/ui)
export { RadioGroup } from './RadioGroup';
export type { RadioGroupProps, RadioOption } from './RadioGroup';

// ============================================
// TYPES
// ============================================

/**
 * Props do componente Input
 */
interface InputProps extends InputHTMLAttributes<HTMLInputElement> {
  /** Label exibido acima do input */
  label?: string;
  /** Mensagem de erro exibida abaixo do input (vermelho) */
  error?: string;
  /** Texto de ajuda exibido abaixo do input (cinza) */
  helperText?: string;
  /** Alias legado de helperText (compat com componentes antigos de forms/) */
  hint?: string;
  /** Ícone exibido à esquerda dentro do input */
  leftIcon?: React.ReactNode;
  /** Ícone exibido à direita dentro do input */
  rightIcon?: React.ReactNode;
}

// ============================================
// COMPONENT
// ============================================

export const Input = forwardRef<HTMLInputElement, InputProps>(
  ({ className, label, error, helperText, hint, leftIcon, rightIcon, id, disabled, ...props }, ref) => {
    const inputId = id || `input-${Math.random().toString(36).substr(2, 9)}`;
    const resolvedHelperText = helperText ?? hint;

    return (
      <div className="w-full">
        {label && (
          <label
            htmlFor={inputId}
            className="block text-sm font-medium text-foreground mb-1"
          >
            {label}
          </label>
        )}

        <div className="relative">
          {leftIcon && (
            <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none text-muted-foreground">
              {leftIcon}
            </div>
          )}

          <input
            ref={ref}
            id={inputId}
            disabled={disabled}
            className={cn(
              'flex h-9 w-full rounded-lg border border-input bg-transparent px-3 py-1 text-sm shadow-sm transition-colors',
              'file:border-0 file:bg-transparent file:text-sm file:font-medium',
              // [TMF9-55436] Placeholder: text-muted-foreground (#a6a6a6 em dark)
              // dá apenas 2.43:1 sobre fundo branco do input (forçado em index.css).
              // Usamos gray-500 em ambos modos pois fundo do input é branco em
              // light E dark — atinge 4.83:1 (WCAG AA, atende AAA p/ UI).
              'placeholder:text-gray-500',
              'focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-ring',
              'disabled:cursor-not-allowed disabled:opacity-50',
              error && 'border-destructive focus-visible:ring-destructive',
              leftIcon && 'pl-10',
              rightIcon && 'pr-10',
              className
            )}
            {...props}
          />

          {rightIcon && (
            <div className="absolute inset-y-0 right-0 pr-3 flex items-center text-muted-foreground">
              {rightIcon}
            </div>
          )}
        </div>

        {error && <p className="mt-1 text-sm text-destructive">{error}</p>}
        {resolvedHelperText && !error && (
          <p className="mt-1 text-sm text-muted-foreground">{resolvedHelperText}</p>
        )}
      </div>
    );
  }
);

Input.displayName = 'Input';

// ============================================
// TEXTAREA
// ============================================

interface TextareaProps extends React.TextareaHTMLAttributes<HTMLTextAreaElement> {
  label?: string;
  error?: string;
  helperText?: string;
}

export const Textarea = forwardRef<HTMLTextAreaElement, TextareaProps>(
  ({ className, label, error, helperText, id, disabled, ...props }, ref) => {
    const textareaId = id || `textarea-${Math.random().toString(36).substr(2, 9)}`;

    return (
      <div className="w-full">
        {label && (
          <label
            htmlFor={textareaId}
            className="block text-sm font-medium text-foreground mb-1"
          >
            {label}
          </label>
        )}

        <textarea
          ref={ref}
          id={textareaId}
          disabled={disabled}
          className={cn(
            'flex w-full rounded-lg border border-input bg-transparent px-3 py-2 text-sm shadow-sm transition-colors resize-y',
            // [TMF9-55436] Placeholder gray-500 (4.83:1 sobre branco) — WCAG AA
            'placeholder:text-gray-500',
            'focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-ring',
            'disabled:cursor-not-allowed disabled:opacity-50',
            error && 'border-destructive focus-visible:ring-destructive',
            className
          )}
          {...props}
        />

        {error && <p className="mt-1 text-sm text-destructive">{error}</p>}
        {helperText && !error && (
          <p className="mt-1 text-sm text-muted-foreground">{helperText}</p>
        )}
      </div>
    );
  }
);

Textarea.displayName = 'Textarea';

// ============================================
// SELECT
// ============================================

interface SelectProps extends React.SelectHTMLAttributes<HTMLSelectElement> {
  label?: string;
  error?: string;
  helperText?: string;
  options?: Array<{ value: string | number; label: string }>;
}

export const Select = forwardRef<HTMLSelectElement, SelectProps>(
  ({ className, label, error, helperText, options = [], children, id, disabled, ...props }, ref) => {
    const selectId = id || `select-${Math.random().toString(36).substr(2, 9)}`;

    return (
      <div className="w-full">
        {label && (
          <label
            htmlFor={selectId}
            className="block text-sm font-medium text-foreground mb-1"
          >
            {label}
          </label>
        )}

        <div className="relative">
          <select
            ref={ref}
            id={selectId}
            disabled={disabled}
            className={cn(
              'flex h-9 w-full rounded-lg border border-input bg-background px-3 py-1 text-sm shadow-sm transition-colors',
              'appearance-none cursor-pointer pr-8',
              'focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-ring',
              'disabled:cursor-not-allowed disabled:opacity-50',
              error && 'border-destructive focus-visible:ring-destructive',
              className
            )}
            {...props}
          >
            {children ||
              options.map((opt) => (
                <option key={opt.value} value={opt.value}>
                  {opt.label}
                </option>
              ))}
          </select>
          <div className="absolute inset-y-0 right-0 pr-3 flex items-center pointer-events-none">
            <svg
              className="h-4 w-4 text-muted-foreground"
              fill="none"
              stroke="currentColor"
              viewBox="0 0 24 24"
            >
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
            </svg>
          </div>
        </div>

        {error && <p className="mt-1 text-sm text-destructive">{error}</p>}
        {helperText && !error && (
          <p className="mt-1 text-sm text-muted-foreground">{helperText}</p>
        )}
      </div>
    );
  }
);

Select.displayName = 'Select';

// ============================================
// CHECKBOX
// ============================================

interface CheckboxProps extends Omit<InputHTMLAttributes<HTMLInputElement>, 'type'> {
  label?: string;
  error?: string;
}

export const Checkbox = forwardRef<HTMLInputElement, CheckboxProps>(
  ({ className, label, error, id, disabled, ...props }, ref) => {
    const checkboxId = id || `checkbox-${Math.random().toString(36).substr(2, 9)}`;

    return (
      <div className="flex items-start">
        <div className="flex items-center h-5">
          <input
            ref={ref}
            id={checkboxId}
            type="checkbox"
            disabled={disabled}
            className={cn(
              'w-4 h-4 rounded border-input text-primary',
              'focus:ring-ring focus:ring-1',
              'bg-background',
              disabled ? 'opacity-50 cursor-not-allowed' : 'cursor-pointer',
              className
            )}
            {...props}
          />
        </div>
        {label && (
          <label
            htmlFor={checkboxId}
            className={cn(
              'ml-2 text-sm text-foreground',
              disabled ? 'opacity-50' : 'cursor-pointer'
            )}
          >
            {label}
          </label>
        )}
        {error && <p className="ml-2 text-sm text-destructive">{error}</p>}
      </div>
    );
  }
);

Checkbox.displayName = 'Checkbox';

// DatePicker foi migrado para @/components/ui/DatePicker (shadcn/ui Calendar + Popover)
// Re-export para manter compatibilidade com imports existentes
export { DatePicker } from './DatePicker';

export default Input;
